// updated auth helper (adjust path/name to your project if needed)
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendEmailVerification, sendPasswordResetEmail, signOut, GoogleAuthProvider, signInWithPopup, updateProfile, deleteUser } from 'firebase/auth';
import { getFirestore, doc, setDoc } from 'firebase/firestore';
import { app } from '@lib/firebase';  // Ensure this alias '@lib/firebase' is correctly configured in your Vite config.

const auth = getAuth(app);
const db = getFirestore(app);
const googleProvider = new GoogleAuthProvider();

/**
 * 🔹 Simpan user ke Firestore
 * - Force refresh token before write to avoid "missing permissions" race
 * - Use setDoc with merge: true (no preliminary getDoc) to simplify
 */
const saveUserToFirestore = async (user) => {
  if (!user) throw new Error("No user provided to saveUserToFirestore");

  try {
    // Ensure token is fresh so Firestore security rules see request.auth
    try {
      await user.getIdToken(true);
    } catch (tokenErr) {
      // token refresh failed: log but continue, setDoc may still fail and be caught below
      console.warn("Failed to refresh ID token before Firestore write:", tokenErr?.message || tokenErr);
    }

    const userRef = doc(db, "users", user.uid);
    await setDoc(
      userRef,
      {
        uid: user.uid,
        name: user.displayName || "",
        email: user.email || "",
        photoURL: user.photoURL || "",
        createdAt: new Date(),
        role: "user",
      },
      { merge: true }
    );
    console.log("User saved to Firestore:", user.uid);
  } catch (error) {
    console.error("❌ Gagal menyimpan user:", error);
    // rethrow so caller can surface error (e.g. show message to user)
    throw error;
  }
};

/**
 * 🔹 Register user & kirim email verifikasi
 */
export const registerAndSendVerification = async (email, name, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    await updateProfile(user, { displayName: name });
    await sendEmailVerification(user);

    // Ensure auth state and token are ready before writing user doc
    // reload currentUser and refresh token
    try {
      await auth.currentUser.reload();
    } catch (reloadErr) {
      console.warn("Failed to reload user after create:", reloadErr?.message || reloadErr);
    }

    try {
      await saveUserToFirestore(user);
    } catch (saveErr) {
      // If saving user document fails due to permissions/rules, we still sent verification email.
      // Do NOT throw error, so the user sees the success message and checks their email.
      console.warn("Saving user to Firestore during registration passed (likely permission issue for unverified user):", saveErr);
    }

    console.log(`Email verifikasi dikirim ke: ${email}`);
    return { message: "Silakan cek email Anda untuk verifikasi sebelum login.", user };
  } catch (error) {
    console.error("Error register:", error);
    const errorCode = error.code || error.message || "unknown_error";
    // map Firebase codes to friendly messages
    switch (errorCode) {
      case "auth/email-already-in-use":
      case "auth/email-already-exists":
        throw new Error("Email sudah terdaftar. Silakan login atau gunakan email lain.");
      case "auth/invalid-email":
        throw new Error("Format email tidak valid.");
      case "auth/weak-password":
        throw new Error("Kata sandi terlalu lemah. Gunakan kata sandi yang lebih kuat.");
      default:
        // if error contains 'permission' or Firestore error, include generic message
        if (error.message && error.message.toLowerCase().includes("permission")) {
          // If we reached here, it means save failed, but we should have caught it above?
          // Actually, if saveUserToFirestore threw inside the try above, it was caught and suppressed.
          // So this default case catches OTHER errors (like createUser failing).
          throw new Error("Terjadi masalah saat mendaftar. Silakan coba lagi.");
        }
        throw new Error("Gagal mendaftar. Silakan coba lagi.");
    }
  }
};

/**
 * 🔹 Cek apakah email sudah diverifikasi & hapus akun jika tidak diverifikasi dalam 2 menit
 */
export const checkVerificationAndDelete = async () => {
  const user = auth.currentUser;
  if (!user) return;

  await user.reload();
  if (!user.emailVerified) {
    try {
      await deleteUser(user);
      console.log("Akun dihapus karena tidak diverifikasi dalam 2 menit.");
    } catch (error) {
      console.error("Gagal menghapus akun:", error);
    }
  }
};

/**
 * 🔹 Kirim ulang email verifikasi
 */
export const sendVerificationEmail = async (user) => {
  if (!user) throw new Error("User tidak ditemukan.");
  try {
    await sendEmailVerification(user);
    return "Email verifikasi telah dikirim. Periksa inbox Anda.";
  } catch (error) {
    console.error("Error mengirim email verifikasi:", error.message);
    throw new Error(error.message);
  }
};

/**
 * 🔹 Cek apakah email sudah diverifikasi
 */
export const checkEmailVerification = async () => {
  if (!auth.currentUser) return false;
  await auth.currentUser.reload();
  return auth.currentUser.emailVerified;
};

/**
 * 🔹 Login dengan email & password
 */
export const login = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    if (user) {
      await user.reload();
      if (!user.emailVerified) {
        throw new Error("Email belum diverifikasi. Silakan cek email Anda.");
      }
      // refresh token before firestore write
      try {
        await user.getIdToken(true);
      } catch (tokenErr) {
        console.warn("Failed to refresh token before saving user:", tokenErr);
      }
      try {
        await saveUserToFirestore(user);
      } catch (err) {
        console.warn("Saving user to Firestore after login failed:", err);
        // don't block login due to Firestore write failure; optionally surface to UI via rethrow
      }
    }
    return user;
  } catch (error) {
    console.error("Error login:", error);
    throw error;
  }
};

/**
 * 🔹 Login dengan Google
 */
export const loginWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    if (user) {
      await user.reload();
      try {
        await saveUserToFirestore(user);
      } catch (err) {
        console.warn("Saving user to Firestore after Google login failed:", err);
      }
      return user;
    }
  } catch (error) {
    console.error("Error login Google:", error);
    const errorCode = error.code || "unknown_error";
    switch (errorCode) {
      case "auth/popup-closed-by-user":
        throw new Error("Login dibatalkan. Silakan coba lagi.");
      default:
        throw new Error("Gagal login dengan Google. Silakan coba lagi.");
    }
  }
};

/**
 * 🔹 Logout user
 */
export const logout = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error("Error logout:", error.message);
    throw new Error(error.message);
  }
};

/**
 * 🔹 Update nama profil setelah registrasi
 */
export const updateUserProfile = async (name) => {
  try {
    if (!auth.currentUser) throw new Error("User tidak ditemukan.");
    await updateProfile(auth.currentUser, { displayName: name });
  } catch (error) {
    console.error("Error update profile:", error.message);
    throw new Error(error.message);
  }
};

/**
 * 🔹 Refresh user data setelah verifikasi
 */
export const refreshUser = async () => {
  if (auth.currentUser) {
    await auth.currentUser.reload();
    return auth.currentUser;
  }
  return null;
};

/**
 * 🔹 Reset password
 */
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return "Email reset password telah dikirim. Periksa inbox Anda.";
  } catch (error) {
    console.error("Error reset password:", error.message);
    const errorCode = error.code || "unknown_error";
    switch (errorCode) {
      case "auth/user-not-found":
        throw new Error("Email tidak terdaftar. Silakan periksa kembali.");
      case "auth/invalid-email":
        throw new Error("Format email tidak valid.");
      default:
        throw new Error("Gagal mengirim link reset. Silakan coba lagi.");
    }
  }
};